import React, { memo, useMemo, useState, useEffect, useCallback, useRef, } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import useMonevoEntry from 'features/borrow/monevo/hooks/useMonevoEntry';
import { selectIsTrackYourMortgageEnabled } from 'features/credit-score/selectors';
import { selectIsGBUser, selectDefaultZeroAmount, selectFeatureFlag, } from 'reducers/selectors';
import { NETWORTH_PAGE_CONTROL_HEIGHT_WITH_PADDING } from 'features/accounts/values';
import { CardView, rem, SettingsItemNavigate } from 'design-system';
import { formatPlural } from 'utils/formatting';
import { overviewColorMap, sections } from '../../constants';
import AccountsSectionCollapsible from './AccountSectionCollapsible';
import i18n from '../../../../i18n';
import BankLoginsHelp from './BankLoginsHelp';
import NetWorthTrackDebtsCard from './NetWorthTrackDebtsCard';
import { isWeb } from '../../../../constants';
function filterNegativeAccounts(data) {
    return Object.keys(data).reduce((acc, key) => {
        if (data[key].negativeAccounts.length > 0) {
            acc[key] =
                data[key];
        }
        return acc;
    }, {});
}
const NetWorthDebtSection = ({ accounts, onRowPress, onOpenLogins, onAddAccount, openAlternative, onOpenUpgrade, onRowLongPress, initialTitle, expandSections, onExpandedSectionLayout, isUnlockedForSpace = true, onContentSizeChange, }) => {
    const navigation = useMainStackNavigation();
    const isGBUser = useAppSelector(selectIsGBUser);
    const showMortgageFinder = useAppSelector((store) => selectFeatureFlag(store, 'mortgages_habito').value);
    const isTrackYourMortgageEnabled = useAppSelector(selectIsTrackYourMortgageEnabled);
    const openMonevo = useMonevoEntry();
    const zero = useAppSelector(selectDefaultZeroAmount);
    const filteredNegativeData = filterNegativeAccounts(accounts);
    const [focus, setFocus] = useState({ sections: expandSections, id: 0 });
    const [sectionShown, setSectionShown] = useState(initialTitle ? initialTitle === 'Net worth' : true);
    useEffect(() => {
        if (!sectionShown) {
            const timeout = setTimeout(() => {
                setSectionShown(true);
            }, 400);
            return () => {
                clearTimeout(timeout);
            };
        }
        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const loanTotal = useMemo(() => accounts['LOAN']
        ? {
            amount: accounts['LOAN'].negativeTotal,
            currency: accounts['LOAN'].array[0]?.currency ?? zero.currency,
        }
        : zero, [accounts, zero]);
    const mortgageTotal = useMemo(() => accounts['MORTGAGE']
        ? {
            amount: accounts['MORTGAGE'].negativeTotal,
            currency: accounts['MORTGAGE'].array[0]?.currency ?? zero.currency,
        }
        : zero, [accounts, zero]);
    const creditCardTotal = useMemo(() => accounts['CREDITCARD']
        ? {
            amount: accounts['CREDITCARD'].negativeTotal,
            currency: accounts['CREDITCARD'].array[0]?.currency ?? zero?.currency,
        }
        : zero, [accounts, zero]);
    const colors = useColors();
    const data = useMemo(() => {
        if (!accounts || !sectionShown)
            return [];
        const baseData = [
            {
                firstCTA: '+ Add a new card',
                secondCTA: isGBUser ? 'find one' : undefined,
                title: sections.creditCards,
                data: accounts['CREDITCARD']
                    ? accounts['CREDITCARD'].negativeAccounts
                    : [],
                total: creditCardTotal,
                color: colors.text[overviewColorMap.Debt],
                type: 'CREDITCARD',
                reverse: true,
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
                openAlternative: () => {
                    openMonevo('CREDIT_CARD');
                },
            },
            {
                firstCTA: '+ Add a loan',
                secondCTA: isGBUser ? 'apply for one' : undefined,
                title: sections.loans,
                data: accounts['LOAN'] ? accounts['LOAN'].negativeAccounts : [],
                total: loanTotal,
                color: colors.text[overviewColorMap.Debt],
                type: 'LOAN',
                reverse: true,
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
                openAlternative,
            },
            ...(isTrackYourMortgageEnabled ||
                accounts['MORTGAGE']?.negativeAccounts.length
                ? [
                    {
                        firstCTA: '+ Add a mortgage',
                        secondCTA: isGBUser && showMortgageFinder ? 'find one' : undefined,
                        title: sections.mortgages,
                        data: accounts['MORTGAGE']
                            ? accounts['MORTGAGE'].negativeAccounts
                            : [],
                        total: mortgageTotal,
                        color: colors.text[overviewColorMap.Debt],
                        type: 'MORTGAGE',
                        reverse: true,
                        onRowPress,
                        onRowLongPress,
                        onOpenLogins,
                        onOpenUpgrade,
                        onAddAccount,
                        openAlternative,
                    },
                ]
                : []),
        ];
        const extra = [];
        if ('CHECKING' in filteredNegativeData) {
            extra.push({
                title: i18n.t('current'),
                data: accounts['CHECKING'].negativeAccounts,
                total: {
                    amount: accounts['CHECKING'].negativeTotal,
                    currency: accounts['CHECKING'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text[overviewColorMap.Debt],
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            });
        }
        if ('SAVINGS' in filteredNegativeData) {
            extra.push({
                title: sections.savings,
                type: 'SAVINGS',
                data: accounts['SAVINGS'].negativeAccounts,
                total: {
                    amount: accounts['SAVINGS'].negativeTotal,
                    currency: accounts['SAVINGS'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text[overviewColorMap.Debt],
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            });
        }
        if ('ALL_INVESTMENT' in filteredNegativeData) {
            extra.push({
                title: sections.investments,
                data: accounts['ALL_INVESTMENT'].negativeAccounts,
                total: {
                    amount: accounts['ALL_INVESTMENT'].negativeTotal,
                    currency: accounts['ALL_INVESTMENT'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text[overviewColorMap.Debt],
                type: 'INVESTMENT',
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
                openAlternative,
            });
        }
        if ('PENSION' in filteredNegativeData) {
            extra.push({
                title: sections.pensions,
                data: accounts['PENSION'].negativeAccounts,
                total: {
                    amount: accounts['PENSION'].negativeTotal,
                    currency: accounts['PENSION'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text[overviewColorMap.Debt],
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            });
        }
        if ('REAL_ESTATE' in filteredNegativeData) {
            extra.push({
                title: sections.realEstate,
                data: accounts['REAL_ESTATE'].negativeAccounts,
                total: {
                    amount: accounts['REAL_ESTATE'].negativeTotal,
                    currency: accounts['REAL_ESTATE'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text[overviewColorMap.Debt],
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            });
        }
        if ('VEHICLE' in filteredNegativeData) {
            extra.push({
                title: sections.vehicles,
                data: accounts['VEHICLE'].negativeAccounts,
                total: {
                    amount: accounts['VEHICLE'].negativeTotal,
                    currency: accounts['VEHICLE'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text[overviewColorMap.Debt],
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            });
        }
        if ('OTHER' in filteredNegativeData) {
            extra.push({
                title: sections.otherAssets,
                data: accounts['OTHER'].negativeAccounts,
                total: {
                    amount: accounts['OTHER'].negativeTotal,
                    currency: accounts['OTHER'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text[overviewColorMap.Debt],
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            });
        }
        return [...extra, ...baseData].map((item) => item.type && focus.sections?.includes(item.type)
            ? { ...item, isCollapsedByDefault: false }
            : item);
    }, [
        accounts,
        colors.text,
        creditCardTotal,
        filteredNegativeData,
        focus.sections,
        isGBUser,
        isTrackYourMortgageEnabled,
        showMortgageFinder,
        loanTotal,
        mortgageTotal,
        onAddAccount,
        onOpenLogins,
        onOpenUpgrade,
        onRowLongPress,
        onRowPress,
        openAlternative,
        openMonevo,
        sectionShown,
        zero.currency,
    ]);
    const hiddenCount = accounts.HIDDEN.negativeAccounts.length;
    const closedCount = accounts.CLOSED.negativeAccounts.length;
    const onPressHidden = useCallback(() => navigation.navigate('HiddenAccountsOverview', { title: 'Debt' }), [navigation]);
    const onPressClosed = useCallback(() => navigation.navigate('ClosedAccountsOverview', { title: 'Debt' }), [navigation]);
    const scrollTargetType = useMemo(() => data.find((item) => item.type && focus.sections?.includes(item.type) && item.data.length)?.type, [data, focus.sections]);
    const scrollTargetRef = useRef(null);
    const didReportScrollTarget = useRef(false);
    const onScrollTargetLayout = useCallback(() => {
        if (didReportScrollTarget.current || !scrollTargetRef.current)
            return;
        didReportScrollTarget.current = true;
        onExpandedSectionLayout?.(scrollTargetRef.current);
    }, [onExpandedSectionLayout]);
    const onTrackingDone = useCallback(() => {
        didReportScrollTarget.current = false;
        setFocus((prev) => ({ sections: ['LOAN', 'MORTGAGE'], id: prev.id + 1 }));
    }, []);
    const renderItem = useCallback(({ item }) => {
        const section = (<AccountsSectionCollapsible {...item} blur={!isUnlockedForSpace}/>);
        if (!scrollTargetType || item.type !== scrollTargetType)
            return section;
        return (<View ref={scrollTargetRef} collapsable={false} onLayout={onScrollTargetLayout}>
          {section}
        </View>);
    }, [isUnlockedForSpace, onScrollTargetLayout, scrollTargetType]);
    const ListFooter = useMemo(() => (<>
        <NetWorthTrackDebtsCard onTrackingDone={onTrackingDone}/>
        {hiddenCount > 0 ? (<CardView noMarginTop>
            <SettingsItemNavigate onPress={onPressHidden} style={styles.webPadding} title={`${hiddenCount} ${formatPlural('hidden account', 'hidden accounts', hiddenCount)}`}/>
          </CardView>) : null}
        {closedCount > 0 ? (<CardView noMarginTop>
            <SettingsItemNavigate onPress={onPressClosed} style={styles.webPadding} title={`${closedCount} ${formatPlural('closed account', 'closed accounts', closedCount)}`}/>
          </CardView>) : null}
        <BankLoginsHelp />
      </>), [hiddenCount, closedCount, onPressHidden, onPressClosed, onTrackingDone]);
    return (<FlatList key={focus.id} data={data} scrollEnabled={false} initialNumToRender={2} windowSize={2} renderItem={renderItem} ListFooterComponent={ListFooter} onContentSizeChange={onContentSizeChange} style={styles.container}/>);
};
export default memo(NetWorthDebtSection);
const styles = StyleSheet.create({
    container: {
        ...(!isWeb && {
            marginTop: NETWORTH_PAGE_CONTROL_HEIGHT_WITH_PADDING,
        }),
    },
    webPadding: {
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
});
